import { getAsset } from "@aster/api/assets";
import styles from "./Money.module.css";

/**
 * <Money> — the canonical amount renderer.
 *
 * Contract (per web-build-spec §9):
 *
 *   - `value` is a `bigint` in the asset's MINOR units. Never a
 *     formatted string; never a number. bigint is chosen because
 *     ETH quantities can exceed Number.MAX_SAFE_INTEGER easily
 *     (10^18 wei per token). Server crosses the wire as
 *     `{ amount: "128460", asset: "USD" }`; helpers in
 *     `@aster/api/money` convert both ways.
 *
 *   - `intent` decides the color. `sign` does not. An outgoing
 *     payment is `--text-primary` with a leading minus — the sign
 *     itself carries direction, not red. Incoming money is `credit`.
 *     Failed and reversed amounts are `muted` (`--text-tertiary`) —
 *     the row's StatusChip already carries `--danger` for failures;
 *     duplicating it on the amount would be shouting the same thing
 *     twice.
 *
 *   - `size` picks a scale: `hero` for the balance, `amount` for
 *     table rows, `body` for inline mentions.
 *
 *   - `loading` dims the value to 40% and shows an inline spinner
 *     WITHOUT changing layout. This is why the balance never
 *     skeletons — the previous number stays visible while it
 *     refreshes; a template skeleton would blank it.
 *
 *   - Screen-reader label spells the amount out fully with the
 *     asset symbol. Sighted users see tabular figures; SR users
 *     get "twelve thousand four hundred eighty dollars and 36 cents".
 */

type MoneyIntent = "default" | "credit" | "muted";
type MoneySize = "hero" | "amount" | "body";

interface MoneyProps {
  value: bigint;
  asset: string;
  intent?: MoneyIntent;
  size?: MoneySize;
  loading?: boolean;
  /** Force show a leading + for positive values (used in change readouts). */
  showPlus?: boolean;
  className?: string;
}

const GROUP_SEP = ",";
const DECIMAL_SEP = ".";

/**
 * Split a minor-unit bigint into (whole, cents) strings, with the
 * decimal alignment set by the asset's `displayDecimals`.
 *
 * Returns strings — no floating point ever touches this function.
 */
function partitionAmount(v: bigint, displayDecimals: number, decimals: number) {
  const isNegative = v < 0n;
  const abs = isNegative ? -v : v;
  const asString = abs.toString().padStart(decimals + 1, "0");
  const wholeStr = asString.slice(0, asString.length - decimals) || "0";
  const fracStr = decimals === 0 ? "" : asString.slice(-decimals);
  // Show only displayDecimals of the fraction. For USD (dec=2, disp=2)
  // that's the whole thing; for ETH (dec=18, disp=6) we truncate.
  const displayFrac = displayDecimals === 0 ? "" : fracStr.slice(0, displayDecimals);
  return { isNegative, wholeStr, displayFrac };
}

function groupThousands(whole: string): string {
  const chars = whole.split("");
  const out: string[] = [];
  for (let i = 0; i < chars.length; i++) {
    if (i > 0 && (chars.length - i) % 3 === 0) out.push(GROUP_SEP);
    out.push(chars[i]);
  }
  return out.join("");
}

/** Spell the amount out for screen readers. Simple: "1234.56 USD". */
function spellAmount(
  isNegative: boolean,
  whole: string,
  frac: string,
  symbol: string,
): string {
  const sign = isNegative ? "negative " : "";
  const wholePart = groupThousands(whole);
  const fracPart = frac ? `${DECIMAL_SEP}${frac}` : "";
  return `${sign}${wholePart}${fracPart} ${symbol}`;
}

export function Money({
  value,
  asset,
  intent = "default",
  size = "amount",
  loading = false,
  showPlus = false,
  className,
}: MoneyProps) {
  const registry = getAsset(asset);
  const symbol = registry.symbol;
  const isFiat = registry.kind === "fiat";
  const { isNegative, wholeStr, displayFrac } = partitionAmount(
    value,
    registry.displayDecimals,
    registry.decimals,
  );

  // For fiat we prefix with the symbol ($, €). For crypto the symbol
  // renders as a suffix ("0.1482 BTC") — cryptocurrency amounts read
  // more naturally that way and it's the convention across trading UIs.
  const prefix = isFiat ? symbol : "";
  const suffix = isFiat ? "" : ` ${symbol}`;

  const sign = isNegative ? "−" : showPlus && value > 0n ? "+" : "";
  const wholeDisplay = groupThousands(wholeStr);

  const spelled = spellAmount(isNegative, wholeStr, displayFrac, symbol);

  const classes = [
    styles.money,
    styles[intent],
    styles[size],
    loading ? styles.loading : null,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <span className={classes} aria-live={loading ? "polite" : undefined}>
      <span aria-hidden>
        {sign}
        {prefix}
        {wholeDisplay}
      </span>
      {displayFrac ? (
        <span aria-hidden className={styles.cents}>
          {DECIMAL_SEP}
          {displayFrac}
        </span>
      ) : null}
      {suffix ? <span aria-hidden>{suffix}</span> : null}
      <span className={styles.sr}>{spelled}</span>
      {loading ? <span aria-hidden className={styles.spinner} /> : null}
    </span>
  );
}
